// Homepage with Navy/Gold Theme
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { seriesConfig } from '../config/series.generated'
import { BookOpen, DollarSign, Star, Users, Award, CheckCircle, Gift, Clock, ArrowRight, Sparkles, KeyRound } from 'lucide-react'
import { LEARN_LIBRARY_ONE_TIME_GBP, LEARN_SUBSCRIPTION_PRICE_GBP_YEAR } from '../config/subscription'

/** Series slugs to highlight as "Latest arrivals" (shown first with a badge). */
const LATEST_ARRIVAL_SERIES_SLUGS = ['commodities-energy']
import SeriesIcon from '../components/ui/SeriesIcon'
import SEOHead from '../components/seo/SEOHead'
import StructuredData from '../components/seo/StructuredData'
import citationContent from '../content/marketing/citation-pages.json'

export default function HomePage() {
  const totalManuals = seriesConfig.reduce((sum, series) => sum + series.manuals.length, 0)
  const totalSeries = seriesConfig.length
  const averageMinutes = 80
  const totalHours = Math.round((totalManuals * averageMinutes) / 60)

  // Get free sample modules
  const freeModules = seriesConfig.flatMap(series =>
    series.manuals.filter((manual: any) => manual.free === true || manual.price === 0)
  )

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHead
        title={citationContent.homepage.title}
        description={citationContent.homepage.description}
        canonicalUrl="https://learn.rondanini.com/"
        ogType="website"
        keywords={[
          'treasury learning infrastructure',
          'trusted treasury education',
          'corporate treasury training',
          'Professional Treasury Education',
          'corporate treasury',
          'financial education',
          'FX trading',
          'foreign exchange',
          'derivatives',
          'risk management',
          'quantitative finance',
          'treasury',
          'money markets',
          'professional finance courses'
        ]}
      />

      {/* Structured Data for Website */}
      <StructuredData type="website" />

    <div className="min-h-screen">
      {/* Hero Section - Navy Gradient */}
      <section className="bg-gradient-to-br from-navy-800 via-navy-900 to-navy-800 text-white">
        <div className="max-w-7xl mx-auto container-padding py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-serif text-4xl md:text-6xl font-bold mb-6 text-white">
              Master Financial Markets
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Professional-grade education on Foreign Exchange, Interest Rates, Liquidity Management, 
              Collateral Risk, and Treasury Systems. Learn from industry experts with real-world experience.
            </p>
            
            {/* Key Benefits */}
            <div className="grid md:grid-cols-4 gap-4 mb-12 max-w-5xl mx-auto">
              <div className="flex items-center justify-center space-x-3 bg-green-600/80 rounded-lg p-4 border border-green-500">
                <Gift className="w-6 h-6 text-white" />
                <span className="text-white font-semibold">Free samples</span>
              </div>
              <div className="flex items-center justify-center space-x-3 bg-navy-700/50 rounded-lg p-4 border border-navy-600">
                <BookOpen className="w-6 h-6 text-gold-400" />
                <span className="text-gray-200">Professional Content</span>
              </div>
              <div className="flex items-center justify-center space-x-3 bg-navy-700/50 rounded-lg p-4 border border-navy-600">
                <KeyRound className="w-6 h-6 text-gold-400 shrink-0" />
                <span className="text-gray-200 text-center text-sm sm:text-base">
                  Library: £{LEARN_SUBSCRIPTION_PRICE_GBP_YEAR}/yr or £{LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)} once
                </span>
              </div>
              <div className="flex items-center justify-center space-x-3 bg-navy-700/50 rounded-lg p-4 border border-navy-600">
                <Award className="w-6 h-6 text-gold-400" />
                <span className="text-gray-200">Industry Experts</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/subscribe"
                className="bg-gold-600 text-navy-800 px-8 py-4 rounded-lg font-semibold hover:bg-gold-500 transition-all hover:-translate-y-0.5 hover:shadow-gold flex items-center justify-center"
              >
                Get full library access
              </Link>
              <a
                href="#free-modules"
                className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-500 transition-all hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center"
              >
                <Gift className="w-5 h-5 mr-2" />
                Try free samples
              </a>
              <Link
                to="/series/all"
                className="border-2 border-gold-400 text-gold-400 px-8 py-4 rounded-lg font-semibold hover:bg-gold-400 hover:text-navy-800 transition-all"
              >
                Browse catalogue
              </Link>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/toolkit"
                className="bg-white/10 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all"
              >
                Cash FX Toolkit
              </Link>
              <a
                href="/derivatives-toolkits"
                className="bg-gold-600 text-navy-800 px-6 py-3 rounded-lg font-semibold hover:bg-gold-500 transition-all"
              >
                FX Derivatives Toolkits
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-navy-900 border-t border-navy-700" aria-label="Treasury learning pages">
        <div className="max-w-7xl mx-auto container-padding py-10">
          <h2 className="font-serif text-2xl font-bold text-white mb-6">Treasury learning</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {citationContent.pages.map((page) => (
              <Link
                key={page.slug}
                to={`/${page.slug}`}
                className="block rounded-lg border border-navy-600 bg-navy-800 px-4 py-5 text-white hover:border-gold-400 hover:text-gold-300 transition-colors"
              >
                <span className="font-semibold">{page.metaTitle}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Stats */}
      <section className="py-16 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-gold-600 dark:text-gold-400 mb-2">
                {totalManuals}+
              </div>
              <div className="text-gray-600 dark:text-gray-400">Expert minimanuals</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gold-600 dark:text-gold-400 mb-2">
                {totalSeries}
              </div>
              <div className="text-gray-600 dark:text-gray-400">Specialised Series</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gold-600 dark:text-gold-400 mb-2">
                £{LEARN_SUBSCRIPTION_PRICE_GBP_YEAR}
              </div>
              <div className="text-gray-600 dark:text-gray-400">Annual library</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gold-600 dark:text-gold-400 mb-2">
                {totalHours}+
              </div>
              <div className="text-gray-600 dark:text-gray-400">Hours Content</div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Arrivals - highlighted series (high up for marketing) */}
      {LATEST_ARRIVAL_SERIES_SLUGS.some(slug => seriesConfig.some((s: { slug: string }) => s.slug === slug)) && (
        <section id="latest-arrivals" className="py-12 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-amber-900/20 border-y border-amber-200 dark:border-amber-800">
          <div className="max-w-7xl mx-auto container-padding">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center justify-center gap-3 mb-8"
            >
              <Sparkles className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              <h2 className="font-serif text-3xl font-bold text-navy-800 dark:text-white">
                Latest arrivals
              </h2>
              <Sparkles className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </motion.div>
            <div className="grid lg:grid-cols-2 gap-8">
              {seriesConfig
                .filter((s: { slug: string }) => LATEST_ARRIVAL_SERIES_SLUGS.includes(s.slug))
                .map((series: typeof seriesConfig[0], index: number) => (
                  <motion.div
                    key={series.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white dark:bg-navy-900 rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border-2 border-amber-400 dark:border-amber-600 overflow-hidden group hover:-translate-y-1"
                  >
                    <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <SeriesIcon icon={series.icon} size="xl" />
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="px-3 py-1 rounded-full text-xs font-bold uppercase bg-white text-amber-700 dark:bg-amber-200 dark:text-amber-900">
                                New
                              </span>
                              <h3 className="text-2xl font-serif font-bold text-white group-hover:text-amber-200 transition-colors">
                                {series.title}
                              </h3>
                            </div>
                            <p className="text-amber-100 text-sm mt-1">
                              {series.manuals.length} minimanuals • Included with library access
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                        {series.description}
                      </p>
                      <Link
                        to={`/series/${series.slug}`}
                        className="block w-full bg-amber-600 hover:bg-amber-500 text-navy-800 text-center py-3 rounded-lg font-semibold transition-all hover:-translate-y-0.5"
                      >
                        View series
                      </Link>
                    </div>
                  </motion.div>
                ))}
            </div>
          </div>
        </section>
      )}

      {/* FREE Sample Modules Section */}
      {freeModules.length > 0 && (
        <section id="free-modules" className="py-20 bg-gradient-to-br from-green-600 via-emerald-700 to-green-800">
          <div className="max-w-7xl mx-auto container-padding">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center bg-white/20 rounded-full px-6 py-2 mb-6">
                <Gift className="w-5 h-5 text-white mr-2" />
                <span className="text-white font-semibold">Try before you subscribe</span>
              </div>
              <h2 className="font-serif text-4xl font-bold text-white mb-6">
                Free sample minimanuals
              </h2>
              <p className="text-xl text-green-100 max-w-3xl mx-auto">
                Experience our professional-grade content quality with these complimentary minimanuals. Free to read on
                the site — the full catalogue is only available via subscription or one-time library purchase.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {freeModules.map((manual: any, index: number) => (
                <motion.div
                  key={manual.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-xl overflow-hidden hover:-translate-y-2 transition-all duration-300"
                >
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-4">
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 bg-white text-green-700 rounded-full text-xs font-bold uppercase flex items-center">
                        <Gift className="w-3 h-3 mr-1" />
                        FREE
                      </span>
                      <span className="px-3 py-1 bg-white/20 text-white rounded-full text-xs font-semibold uppercase">
                        {manual.difficulty}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="font-serif text-xl font-bold text-navy-800 mb-3">
                      {manual.title}
                    </h3>
                    <p className="text-gray-600 mb-4 text-sm line-clamp-3">
                      {manual.summary}
                    </p>
                    
                    <div className="flex items-center text-sm text-gray-500 mb-6">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{manual.duration}</span>
                    </div>

                    <Link
                      to={`/${manual.slug}`}
                      className="w-full bg-green-600 hover:bg-green-700 text-white text-center py-3 rounded-lg font-semibold transition-all flex items-center justify-center hover:-translate-y-0.5 hover:shadow-lg"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Read Free Module
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center mt-10"
            >
              <p className="text-green-100 text-lg">
                These samples stay free to read on the site. No credit card required.
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* All Series Grid */}
      <section className="py-20 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl font-bold text-navy-800 dark:text-white mb-6">
              Complete Professional Library
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Comprehensive coverage across major areas of financial markets. Paid minimanuals are not sold individually
              — you get the entire library for £{LEARN_SUBSCRIPTION_PRICE_GBP_YEAR}/year or £
              {LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)} one-time.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {seriesConfig.map((series, index) => (
              <motion.div
                key={series.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white dark:bg-navy-900 rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-gray-200 dark:border-navy-700 overflow-hidden group hover:-translate-y-1"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-navy-800 to-navy-900 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <SeriesIcon icon={series.icon} size="xl" />
                      <div>
                        <h3 className="text-2xl font-serif font-bold text-white group-hover:text-gold-400 transition-colors">
                          {series.title}
                        </h3>
                        <div className="flex items-center space-x-3 mt-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                            series.difficulty === 'beginner' 
                              ? 'bg-green-500 text-white'
                              : series.difficulty === 'intermediate'
                              ? 'bg-gold-600 text-navy-800'
                              : 'bg-red-500 text-white'
                          }`}>
                            {series.difficulty}
                          </span>
                          {LATEST_ARRIVAL_SERIES_SLUGS.includes(series.slug) && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase bg-amber-400 text-amber-900 flex items-center">
                              <Sparkles className="w-3 h-3 mr-1" />
                              Latest
                            </span>
                          )}
                          {series.featured && (
                            <div className="flex items-center text-sm text-gold-400">
                              <Star className="w-4 h-4 mr-1 fill-current" />
                              Popular
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right hidden sm:block">
                      <div className="text-xs text-gray-400 uppercase tracking-wide">Access</div>
                      <div className="text-sm font-bold text-gold-400 leading-tight">
                        Via library
                        <br />
                        <span className="text-xs font-normal text-gray-400">not per module</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                    {series.description}
                  </p>
                  
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-navy-700 dark:text-gray-300">
                        Minimanuals in this series:
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {series.manuals.length} minimanuals • library only
                      </span>
                    </div>
                    <div className="space-y-2">
                      {series.manuals.slice(0, 3).map((manual) => (
                        <div key={manual.slug} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          <span className="truncate">{manual.title}</span>
                        </div>
                      ))}
                      {series.manuals.length > 3 && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 ml-6">
                          +{series.manuals.length - 3} more modules...
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to={`/series/${series.slug}`}
                      className="flex-1 bg-navy-800 hover:bg-navy-900 text-white text-center py-3 rounded-lg font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg"
                    >
                      View series
                    </Link>
                    <Link
                      to="/subscribe"
                      className="flex-1 bg-gold-600 hover:bg-gold-500 text-navy-800 text-center py-3 rounded-lg font-semibold transition-all hover:-translate-y-0.5 hover:shadow-gold"
                    >
                      Get library access
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl font-bold text-navy-800 dark:text-white mb-6">
              Why Choose Rondanini Publishing?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Professional-grade financial education from industry experts with real-world trading and treasury experience.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-navy-100 dark:bg-navy-800 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-gold-500">
                <Users className="w-8 h-8 text-navy-800 dark:text-gold-400" />
              </div>
              <h3 className="font-serif text-xl font-bold text-navy-800 dark:text-white mb-4">
                Industry Experts
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Content created by professionals with decades of experience in investment banking, 
                corporate treasury, and risk management at top-tier institutions.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-navy-100 dark:bg-navy-800 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-gold-500">
                <BookOpen className="w-8 h-8 text-navy-800 dark:text-gold-400" />
              </div>
              <h3 className="font-serif text-xl font-bold text-navy-800 dark:text-white mb-4">
                Practical Content
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Real-world applications with case studies, calculations, and examples from actual market 
                situations. Learn what works in practice, not just theory.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-navy-100 dark:bg-navy-800 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-gold-500">
                <DollarSign className="w-8 h-8 text-navy-800 dark:text-gold-400" />
              </div>
              <h3 className="font-serif text-xl font-bold text-navy-800 dark:text-white mb-4">
                One library, one price
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Pay once for the full catalogue or subscribe annually — professional-grade minimanuals without
                per-module checkout or surprise add-ons.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action - Gold Gradient */}
      <section className="py-20 bg-gradient-to-r from-navy-800 via-navy-900 to-navy-800">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl font-bold text-white mb-6">
              Ready to Advance Your Career?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Join thousands of financial professionals who have enhanced their expertise with 
              our comprehensive modules. Start your learning journey today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/subscribe"
                className="bg-gold-600 text-navy-800 px-8 py-4 rounded-lg font-semibold hover:bg-gold-500 transition-all hover:-translate-y-0.5 hover:shadow-gold"
              >
                View plans & checkout
              </Link>
              <Link
                to="/series/all"
                className="border-2 border-gold-400 text-gold-400 px-8 py-4 rounded-lg font-semibold hover:bg-gold-400 hover:text-navy-800 transition-all"
              >
                Browse the catalogue
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  )
}
